import React, { useRef, useEffect } from "react";
import Button from "../ui/Button";
import { motion } from "framer-motion";
import { useMouseParallax } from "../../utils/mouseParallax";
import { useLocation, useNavigate } from "react-router-dom";
import HeroTitle from "../ui/HeroTitle";

const Hero = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const heroRef = useRef(null);

    // Use the shared mouse parallax hook
    useMouseParallax({ elementRef: heroRef, strength: 50 });

    const scrollToSection = (sectionId, e) => {
        e.preventDefault();

        if (location.pathname !== "/") {
            navigate(`/#${sectionId}`);
            return;
        }

        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: "smooth" });
            window.history.pushState({}, "", `/#${sectionId}`);
        }
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { delayChildren: 0.3, staggerChildren: 0.2 },
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.6, ease: "easeOut" },
        },
    };

    return (
<motion.section
    ref={heroRef}
    className="relative min-h-[500px] h-screen flex items-center justify-center px-4 sm:px-6"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.8, ease: "easeInOut" }}
>
    {/* Responsive floating elements */}
    <div className="absolute top-1/4 right-1/4 w-36 sm:w-48 md:w-72 h-36 sm:h-48 md:h-72 rounded-full bg-indigo-300/10 blur-2xl floating-element"></div>
    <div className="absolute bottom-1/3 left-1/4 w-48 sm:w-64 md:w-96 h-48 sm:h-64 md:h-96 rounded-full bg-pink-300/10 blur-3xl floating-element"></div>
    <div className="absolute top-1/3 left-1/5 w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 rounded-full bg-blue-300/10 blur-xl floating-element"></div>

    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
            className="max-w-3xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight text-gradient from-fuchsia-500 to-purple-600 text-transparent bg-clip-text pb-6"
                variants={itemVariants}
            >
                Agence web & mobile solutions digitales
            </motion.h1>

            <motion.p
                className="text-sm sm:text-base lg:text-xl mb-6 sm:mb-8 md:mb-12 mx-auto max-w-2xl text-gray-700 glass-card p-3 sm:p-4"
                variants={itemVariants}
            >
                Experts en création de sites web,
                applications mobiles, référencement SEO et solutions e-commerce
            </motion.p>

            <motion.div
                className="flex flex-col sm:flex-row justify-center items-center gap-4"
                variants={itemVariants}
            >
                <Button
                    variant="black"
                    className="w-full sm:w-auto flex items-center justify-center sm:justify-start space-x-2.5 group py-3 sm:py-3.5 px-6 sm:px-8 text-sm sm:text-base"
                    onClick={(e) => scrollToSection("services", e)}
                >
                    <span>Voir nos services</span>
                    <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 transform group-hover:translate-y-1.5 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 5v14m-7-7l7 7m0 0l7-7"
                        />
                    </svg>
                </Button>
            </motion.div>
            
            {/* Showcase images - similar to what's shown in the screenshot */}
<motion.div 
    className="mt-12 md:mt-16 relative"
    variants={itemVariants}
>
    <div className="grid grid-cols-3 gap-4 mx-auto">
        <div className="col-span-1 transform -rotate-6 shadow-lg rounded-lg overflow-hidden hover:scale-105 transition-transform duration-300">
            <img 
                src="/images/2.png"
                alt="Application Mobile Premium" 
                className="w-full h-auto object-cover aspect-[4/3]" 
            />
        </div>
        <div className="col-span-1 transform translate-y-8 shadow-lg rounded-lg overflow-hidden hover:scale-105 transition-transform duration-300">
            <img 
                src="/images/test.png"
                alt="Site Web Design" 
                className="w-full h-auto object-cover aspect-[4/3]" 
            />
        </div>
        <div className="col-span-1 transform rotate-6 shadow-lg rounded-lg overflow-hidden hover:scale-105 transition-transform duration-300">
            <img 
                src="/images/4.png"
                alt="Analytics & Performance" 
                className="w-full h-auto object-cover aspect-[4/3]" 
            />
        </div>
    </div>
</motion.div>
        </motion.div>
    </div>
</motion.section>
    );
};

export default Hero;
